import { useEffect, useState, useRef } from "react";

/*
 * useAudioPeaks
 * -------------
 * Fetches an audio URL, decodes it in a Web Audio context, and downsamples
 * into `barCount` normalised RMS peaks (0..1). Cached per URL in localStorage
 * so subsequent visits are instant. Failures are silent and return null so
 * the caller can fall back to a pseudo-waveform.
 */
const CACHE_PREFIX = "gvg-peaks:v1:";

function readCache(url, barCount) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + `${barCount}:` + url);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr) && arr.length === barCount) return arr;
    return null;
  } catch (e) {
    return null;
  }
}

function writeCache(url, barCount, peaks) {
  try {
    localStorage.setItem(
      CACHE_PREFIX + `${barCount}:` + url,
      JSON.stringify(peaks)
    );
  } catch (e) {
    /* quota exceeded, storage disabled — safe to ignore */
  }
}

async function decodePeaks(url, barCount) {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) throw new Error("Web Audio unsupported");
  const ctx = new AC();
  try {
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const buf = await res.arrayBuffer();
    const audio = await ctx.decodeAudioData(buf.slice(0));
    // Merge channels down to mono
    const chLen = audio.length;
    const mono = new Float32Array(chLen);
    for (let ch = 0; ch < audio.numberOfChannels; ch++) {
      const data = audio.getChannelData(ch);
      for (let i = 0; i < chLen; i++) mono[i] += data[i];
    }
    if (audio.numberOfChannels > 1) {
      const inv = 1 / audio.numberOfChannels;
      for (let i = 0; i < chLen; i++) mono[i] *= inv;
    }
    // Downsample into `barCount` RMS chunks
    const per = Math.max(1, Math.floor(chLen / barCount));
    const raw = new Array(barCount).fill(0);
    for (let b = 0; b < barCount; b++) {
      const start = b * per;
      const end = b === barCount - 1 ? chLen : start + per;
      let sum = 0;
      for (let i = start; i < end; i++) sum += mono[i] * mono[i];
      raw[b] = Math.sqrt(sum / Math.max(1, end - start));
    }
    // Normalise + gentle gamma so quiet parts stay readable
    const max = raw.reduce((m, v) => (v > m ? v : m), 0.0001);
    const peaks = raw.map((v) => {
      const n = v / max;
      return Math.min(1, Math.max(0.12, Math.pow(n, 0.7)));
    });
    return peaks;
  } finally {
    try {
      ctx.close();
    } catch (e) {
      /* noop */
    }
  }
}

export function useAudioPeaks(audioUrl, barCount = 40) {
  const [peaks, setPeaks] = useState(() => (audioUrl ? readCache(audioUrl, barCount) : null));
  const activeUrlRef = useRef(null);

  useEffect(() => {
    if (!audioUrl) {
      setPeaks(null);
      return undefined;
    }
    activeUrlRef.current = audioUrl;

    // Read cache first (in case URL changed)
    const cached = readCache(audioUrl, barCount);
    if (cached) {
      setPeaks(cached);
      return undefined;
    }
    setPeaks(null);

    let cancelled = false;
    decodePeaks(audioUrl, barCount)
      .then((result) => {
        if (cancelled || activeUrlRef.current !== audioUrl) return;
        writeCache(audioUrl, barCount, result);
        setPeaks(result);
      })
      .catch(() => {
        /* silent — caller falls back to pseudo */
      });

    return () => {
      cancelled = true;
    };
  }, [audioUrl, barCount]);

  return peaks;
}
